import { z } from 'zod';
import { baseEntityFields, idSchema, isoDateTimeSchema } from './common.schema';
/**
 * Collector — the on-site Go agent. Registered via one-time claim code;
 * authenticates with a per-collector token (hash stored, never the token).
 * The collector is itself a monitored resource: heartbeat state and
 * self-metrics live here (technical-architecture.md §6).
 */
export const COLLECTOR_STATUSES = [
    /** Created in the portal; claim code not yet redeemed. */
    'pending',
    'active',
    /** Missed heartbeat threshold — down-collector alerting fires. */
    'down',
    'disabled',
];
export const COLLECTOR_CHANNELS = ['stable', 'early'];
export const COLLECTOR_PLATFORMS = ['windows', 'linux'];
/** Self-reported health snapshot from the collector heartbeat. */
export const collectorHealthSchema = z
    .object({
    queueDepth: z.number().int().nonnegative(),
    pollLatencyMsP50: z.number().finite().nonnegative(),
    droppedSinceLastHeartbeat: z.number().int().nonnegative(),
    cpuPercent: z.number().min(0).max(100).optional(),
    memoryMb: z.number().finite().nonnegative().optional(),
})
    .strict();
export const collectorInputSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    clientId: idSchema.optional(),
    channel: z.enum(COLLECTOR_CHANNELS).default('stable'),
})
    .strict();
export const collectorUpdateSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    clientId: idSchema.nullable().optional(),
    channel: z.enum(COLLECTOR_CHANNELS).optional(),
    status: z.enum(['active', 'disabled']).optional(),
})
    .strict();
/** Stored shape: clearable optional fields are materialized as null. */
export const collectorSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable(),
    clientId: idSchema.nullable(),
    channel: z.enum(COLLECTOR_CHANNELS),
    status: z.enum(COLLECTOR_STATUSES),
    /** SHA-256 hash (lowercase hex) of the outstanding claim code;
     *  cleared on redemption. */
    claimCodeHash: z
        .string()
        .regex(/^[a-f0-9]{64}$/, 'must be lowercase SHA-256 hex')
        .nullable(),
    claimExpiresAt: isoDateTimeSchema.nullable(),
    /** SHA-256 hash (lowercase hex) of the current collector token.
     *  Never the token itself. */
    tokenHash: z
        .string()
        .regex(/^[a-f0-9]{64}$/, 'must be lowercase SHA-256 hex')
        .nullable(),
    tokenRotatedAt: isoDateTimeSchema.nullable(),
    platform: z.enum(COLLECTOR_PLATFORMS).nullable(),
    version: z.string().max(64).nullable(),
    hostname: z.string().max(253).nullable(),
    registeredAt: isoDateTimeSchema.nullable(),
    lastHeartbeatAt: isoDateTimeSchema.nullable(),
    health: collectorHealthSchema.nullable(),
})
    .extend(baseEntityFields)
    .strict()
    .superRefine((val, ctx) => {
    // Registration lifecycle invariants:
    //   pending  = claim code outstanding, nothing registered yet
    //   active/down = fully registered (token, platform, version, timestamp)
    //   disabled = either side of registration; no extra constraints
    if (val.status === 'pending') {
        if (val.registeredAt !== null ||
            val.tokenHash !== null ||
            val.platform !== null ||
            val.version !== null ||
            val.hostname !== null ||
            val.lastHeartbeatAt !== null ||
            val.health !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['status'],
                message: 'pending collectors must not carry any registration or runtime state',
            });
        }
        if (val.claimCodeHash === null || val.claimExpiresAt === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['claimCodeHash'],
                message: 'pending collectors require an outstanding claim code with an expiry',
            });
        }
    }
    if (val.status === 'active' || val.status === 'down') {
        if (val.registeredAt === null ||
            val.tokenHash === null ||
            val.platform === null ||
            val.version === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['status'],
                message: `${val.status} collectors must be fully registered (token, platform, version, registeredAt)`,
            });
        }
        // "down" = the down-detection sweep decided this REGISTERED
        // collector is not reporting — either its heartbeats stopped OR
        // it registered and never heartbeated at all. Both are down
        // operationally, so heartbeat fields stay nullable here; the
        // registration gate above (down ⊆ registered) is the invariant.
        // (Revised from an earlier heartbeat-required rule, which made
        // the never-heartbeated case unrepresentable.)
        // Claim codes are single-use and cleared (with their expiry) on
        // redemption — a registered collector still carrying either is a
        // security defect / zombie-claim risk.
        if (val.claimCodeHash !== null || val.claimExpiresAt !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['claimCodeHash'],
                message: 'registered collectors must not carry claim-code state',
            });
        }
    }
});
//# sourceMappingURL=collector.schema.js.map